import { useState, useEffect } from "react";
import "./App.css";
import axios from "axios";
import AdminApp from "./AdminApp";
import PartnerApp from "./PartnerApp";
import EnterpriseApp from "./EnterpriseApp";
import { ForgotPasswordPage, LandingPage, LoginPage, SignupPage, VerifyEmailPage } from "./pages/auth";
import { API, APP_LOGO_ICON, AuthContext, Icon, api, useAuth } from "./lib/core";
import { Dashboard } from "./pages/dashboard";
import { toast } from "sonner";

const AuthProvider = ({ children }) => {
  // Hydrate auth synchronously from localStorage so there is NO loading gate / spinner
  // flash when the page reloads (waking from sleep, returning to the tab, reopening the PWA).
  const [user, setUser] = useState(() => {
    try {
      const savedUser = localStorage.getItem("topit_user");
      return savedUser ? JSON.parse(savedUser) : null;
    } catch {
      return null;
    }
  });
  const login = (authData) => {
    setUser(authData.user);
    // Token lives in an httpOnly cookie now; only the (non-credential) user profile is
    // persisted for instant hydration. Purge any legacy localStorage token.
    localStorage.removeItem("topit_token");
    localStorage.setItem("topit_user", JSON.stringify(authData.user));
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem("topit_token");
    localStorage.removeItem("topit_user");
    api.post("/user/logout").catch(() => {});
  };

  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem("topit_user", JSON.stringify(userData));
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// Main App Component
// Flutterwave post-payment landing page (/payment-callback). Verifies the payment
// (crediting wallet or fulfilling the recharge) and routes the user back into the app.

const PaymentCallback = () => {
  const [state, setState] = useState("verifying"); // verifying | success | pending | failed | error
  const [message, setMessage] = useState("Confirming your payment…");
  const [recharge, setRecharge] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const txRef = params.get("tx_ref");
    const status = params.get("status");
    const isRecharge = (txRef || "").startsWith("TOPIT-RC-");

    if (!txRef) { setState("error"); setMessage("Missing payment reference."); return; }
    if (status === "cancelled") { setState("failed"); setMessage("Payment was cancelled."); return; }
    if (!localStorage.getItem("topit_user") && !localStorage.getItem("topit_token")) {
      setState("error"); setMessage("Please log in again, then we'll confirm your payment.");
      return;
    }

    let cancelled = false;
    const url = isRecharge ? `/payments/flutterwave/recharge/verify/${txRef}` : `/payments/flutterwave/verify/${txRef}`;

    const attempt = async (n) => {
      try {
        const res = await api.get(url);
        const ok = res.data.status === "successful";
        const failed = res.data.status === "failed";
        if (ok) {
          if (cancelled) return;
          setState("success");
          if (isRecharge) {
            const code = res.data.vend?.status_code;
            const delivered = code === "000" || code === "OK";
            if (res.data.recharge) setRecharge({ ...res.data.recharge, delivered });
            setMessage(delivered
              ? "Your recharge was delivered successfully!"
              : "Payment received — your recharge is being processed and will reflect shortly.");
          } else {
            const amt = res.data.amount;
            setMessage(amt ? `Your wallet has been topped up with ₦${Number(amt).toLocaleString()}.` : "Your wallet has been topped up successfully!");
          }
          return;
        }
        if (failed) { if (!cancelled) { setState("failed"); setMessage("Payment failed. Please try again."); } return; }
        // pending — retry a few times
        if (n < 5) { setTimeout(() => attempt(n + 1), 3000); }
        else if (!cancelled) {
          setState("pending");
          setMessage("Your payment is still being confirmed. If you completed it, your balance will update shortly.");
        }
      } catch (err) {
        if (cancelled) return;
        if (err.response?.status === 401 || err.response?.status === 403) {
          setState("error"); setMessage("Please log in again, then we'll confirm your payment.");
        } else {
          setState("error"); setMessage(err.response?.data?.detail || "We couldn't verify your payment automatically.");
        }
      }
    };
    attempt(0);
    return () => { cancelled = true; };
  }, []);

  const icon = state === "success" ? "check_circle" : state === "failed" ? "cancel" : state === "pending" ? "schedule" : state === "error" ? "error" : "progress_activity";
  const color = state === "success" ? "text-green-500" : state === "failed" ? "text-red-500" : state === "pending" ? "text-amber-500" : state === "error" ? "text-red-500" : "text-primary-custom";

  return (
    <div className="min-h-screen bg-background-light flex flex-col items-center justify-center p-4" data-testid="payment-callback">
      <div className="flex flex-col items-center mb-8">
        <div className="flex items-center gap-2">
          <img src={APP_LOGO_ICON} alt="TopIt" className="h-10 w-10 object-contain" />
          <span className="text-2xl font-bold tracking-tight"><span className="text-slate-900">Top</span><span className="text-primary-custom">It</span></span>
        </div>
        <span className="text-slate-400 text-[10px] tracking-wide mt-0.5">Powered by Sineli</span>
      </div>
      <div className="bg-white rounded-3xl border border-slate-200/70 shadow-sm w-full max-w-md p-8 text-center" data-testid={`payment-callback-${state}`}>
        <Icon name={icon} className={`text-6xl ${color} ${state === "verifying" ? "animate-spin" : ""}`} />
        <h1 className="mt-4 text-xl font-bold text-slate-900">
          {state === "success" ? "Payment Successful" : state === "failed" ? "Payment Failed" : state === "pending" ? "Almost There" : state === "error" ? "Verification Needed" : "Confirming Payment"}
        </h1>
        <p className="mt-2 text-sm text-slate-500">{message}</p>
        {state !== "verifying" && (
          <div className="mt-6 space-y-2">
            <button onClick={() => {
              if (recharge) {
                try { localStorage.setItem("topit_recharge_confirmation", JSON.stringify(recharge)); } catch { /* noop */ }
              }
              window.location.href = "/";
            }} data-testid="payment-callback-dashboard" className="w-full h-12 bg-primary-custom hover:bg-primary-dark text-white rounded-xl font-bold transition-all">
              Go to Dashboard
            </button>
            {(state === "failed" || state === "pending") && (
              <button onClick={() => window.location.reload()} data-testid="payment-callback-retry" className="w-full h-11 text-slate-500 hover:text-slate-700 text-sm font-semibold">
                Check again
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};


function App() {
  // Always show landing on every app open
  const [authPage, setAuthPage] = useState("landing");

  // Capture an affiliate/partner code from ?partner=CODE (or ?ref=) on any landing,
  // persist it forever, and record the click. Applied at signup / checkout.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const code = (params.get("partner") || params.get("ref") || "").trim().toUpperCase();
      if (code) {
        localStorage.setItem("topit_partner_code", code);
        axios.post(`${API}/partners/track-click`, { code }).catch(() => {});
      }
      // Customer referral link: /ref/{CODE} or ?ref_code=CODE — stored for auto-apply after login.
      const path = window.location.pathname || "";
      const m = path.match(/^\/ref\/([^/?#]+)/i);
      const refCode = (m ? m[1] : params.get("ref_code") || "").trim();
      if (refCode) {
        localStorage.setItem("topit_ref_code", refCode);
        if (m) window.history.replaceState({}, "", "/");
      }
    } catch { /* noop */ }
  }, []);

  // Partner portal lives at /partner (isolated SPA)
  if (typeof window !== "undefined" && window.location.pathname.startsWith("/partner")) {
    return <PartnerApp />;
  }

  // Enterprise portal lives at /enterprise (isolated SPA)
  if (typeof window !== "undefined" && window.location.pathname.startsWith("/enterprise")) {
    return <EnterpriseApp />;
  }

  // Admin portal lives at /manage (isolated SPA)
  if (typeof window !== "undefined" && window.location.pathname.startsWith("/manage")) {
    return <AdminApp />;
  }

  // Flutterwave returns the user here after payment (?status=&tx_ref=&transaction_id=)
  if (typeof window !== "undefined" && window.location.pathname.startsWith("/payment-callback")) {
    return <PaymentCallback />;
  }

  return (
    <AuthProvider>
      <AppContent authPage={authPage} setAuthPage={setAuthPage} />
    </AuthProvider>
  );
}

// App Content - Uses Auth Context

const AppContent = ({ authPage, setAuthPage }) => {
  const { user } = useAuth();
  const [verifyEmail, setVerifyEmail] = useState("");

  // Auto-apply a pending customer referral code once the user is signed in.
  useEffect(() => {
    if (!user) return;
    const refCode = localStorage.getItem("topit_ref_code");
    if (!refCode) return;
    api.post("/wallet/apply-referral", { referral_code: refCode })
      .then(() => toast.success("Referral code applied! Your friend will be rewarded once you fund & buy."))
      .catch((e) => {
        const d = e?.response?.data?.detail;
        if (d && !String(d).toLowerCase().includes("already")) toast.error(typeof d === "string" ? d : "Couldn't apply referral code");
      })
      .finally(() => localStorage.removeItem("topit_ref_code"));
  }, [user]);

  if (user) {
    return <Dashboard />;
  }

  if (authPage === "landing") {
    return (
      <LandingPage
        onGetStarted={() => setAuthPage("signup")}
        onLogin={() => setAuthPage("login")}
      />
    );
  }

  if (authPage === "signup") {
    return (
      <SignupPage
        onSwitchToLogin={() => setAuthPage("login")}
        onNeedVerification={(email) => { setVerifyEmail(email); setAuthPage("verify-email"); }}
      />
    );
  }

  if (authPage === "verify-email") {
    return (
      <VerifyEmailPage
        email={verifyEmail}
        onBack={() => setAuthPage("login")}
      />
    );
  }

  if (authPage === "forgot-password") {
    return (
      <ForgotPasswordPage
        onBack={() => setAuthPage("login")}
        onSuccess={() => setAuthPage("login")}
      />
    );
  }

  return (
    <LoginPage
      onSwitchToSignup={() => setAuthPage("signup")}
      onForgotPassword={() => setAuthPage("forgot-password")}
      onNeedVerification={(email) => { setVerifyEmail(email); setAuthPage("verify-email"); }}
    />
  );
};

export default App;
